import { Lightning as lng } from '@lightningjs/sdk';
import { EpgListDataSource } from '../../../Business/epg/EpgListDataSource';
import { EpgListFilterDataSource } from '../../../Business/epg/EpgListFilterDataSource';
import { listFilterType } from './FilterState';
import { CmsRibbonDummy } from '../../../components/cms/CmsRibbonDummy';
import { CmsConstants } from '../../../Business/cms/CmsConstants';
import { CmsRibbon } from '../../../components/cms/CmsRibbon';
import { debounced } from '../../../Business/utility/Debounced';

const _constants = {
  margin: 20,
  debounceTime: 300,
};

export class EpgListPage extends lng.Component {
  static _template() {
    return {
      x: 0,
      y: 0,
      clipping: true,
      w: w => w,
      h: h => h,
      Content: {
        w: w => w,
      },
    };
  }

  set isReplayPage(val) {
    this.replay = val;
  }

  _init() {
    // if replay is not initialized
    if (!this.replay) {
      if (this.params && this.params.isReplay) {
        this.replay = this.params.isReplay;
      } else {
        this.replay = false;
      }
    }
    this.focusIdx = 1;
    this._dummyChildren = [];
    this.dataSource = new EpgListDataSource();
    // console.log(this.dataSource, this.params);
    if (this.params.properties) {
      if (this.params.properties.includes('FOCUS_PAST')) {
        this.replay = true;
      }
    }
    if (this.params.menuItem) {
      this.dataSource.logPageView(this.params.backend, this.replay, this.params.menuItem.title);
    } else {
      // fallback if page is opened from jump to list
      this.dataSource.logPageView(this.params.backend, this.replay, 'LIST_PAGE');
    }

    this.currentYvalue = _constants.margin;

    let filtersDS = new EpgListFilterDataSource(this.params.backend, this.params.filterInit, this.replay);
    filtersDS.load().then(() => {
      this.tag('Content').childList.a({
        y: this.currentYvalue,
        signals: { centerVertical: '_centerVertical' },
        contentOffset: CmsConstants.Ribbon.pageContentOffset,
        ...filtersDS.componentDescription(),
      });
      this.currentYvalue += _constants.margin * 2 + filtersDS.height();

      this.currentFilterState = filtersDS.state;
      filtersDS.state.subscribe((type, value) => {
        if (!value && type !== listFilterType.all) return;
        this.reloadContent(filtersDS.state);
      });

      this.reloadContent(filtersDS.state);
      this.debouncedLoad = debounced(this.reloadCurrentlyOnScreen.bind(this), _constants.debounceTime);
    });
  }

  reloadContent(state) {
    this.tag('Content').children = this.tag('Content').children.splice(0, 1);
    let filterState = state.state;
    this.currentYvalue = _constants.margin * 2 + this.tag('Content').children[0].data.height();

    this.dataSource.load(this.params.backend, filterState, this.replay).then(ribbons => {
      // console.log('Reload content', ribbons);
      this.setRibbons(ribbons);
      this.reloadCurrentlyOnScreen(true);
    });
  }

  loadNext() {
    let filterState = this.currentFilterState.state;
    this.dataSource.loadNext(this.params.backend, filterState).then(ribbons => {
      // console.log('loadNext content', ribbons);
      this.setRibbons(ribbons);
    });
  }

  updateHeight() {
    this.tag('Content').h = this.currentYvalue;
  }

  setRibbons(datasources) {
    let ribbonsTag = this.tag('Content');
    // always delete  dummies
    this._dummyChildren.forEach(c => ribbonsTag.childList.remove(c));
    this._dummyChildren = [];

    if (!datasources || datasources.length === 0) return;
    datasources.forEach(ds => {
      ribbonsTag.childList.a({
        y: this.currentYvalue,
        signals: {
          centerVertical: '_centerVertical',
        },
        contentOffset: CmsConstants.Ribbon.pageContentOffset,
        w: this.finalW,
        ...ds.componentDescription(),
      });
      this.currentYvalue += _constants.margin + ds.height();
      this.updateHeight();
    });
    // add dummy
    const _height = datasources[0]?.cardHeight ? datasources[0].cardHeight : 200;
    this._dummyChildren.push(
      ribbonsTag.childList.a({
        y: this.currentYvalue,
        type: CmsRibbonDummy,
        h: _height,
        signals: {
          startLazyLoad: this.loadNext.bind(this),
        },
      })
    );
  }

  reloadCurrentlyOnScreen(reloadAll = false) {
    const children = this.tag('Content').children;
    let activeNotLoadedRibbons = [];
    if (reloadAll) {
      activeNotLoadedRibbons = children.filter(el => el instanceof CmsRibbon && !el.data.isLoaded);
    } else {
      activeNotLoadedRibbons = children.filter(el => el instanceof CmsRibbon && el.__active && !el.data.isLoaded);
    }
    // console.log('all childs ribbons', children);
    // console.log('active not yet loaded ribbons', activeNotLoadedRibbons);
    activeNotLoadedRibbons.map(ribbon =>
      ribbon.data.load().then(() => {
        ribbon.reloadContent();
      })
    );
  }

  _getFocused() {
    let ribbonsTag = this.tag('Content');
    return ribbonsTag.children[this.focusIdx];
  }

  _handleDown() {
    let prevValue = this.focusIdx;
    this.focusIdx = Math.min(this.focusIdx + 1, this._getFocusable().length - 1 - this._dummyChildren.length);
    // this.lazyLoad(this.currentFilterState);
    if (prevValue !== this.focusIdx) {
      this.debouncedLoad();
      return true;
    } else {
      return false;
    }
  }

  _handleUp() {
    let prevValue = this.focusIdx;
    this.focusIdx = Math.max(this.focusIdx - 1, 0);
    if (prevValue !== this.focusIdx) {
      this.debouncedLoad();
      return true;
    } else {
      return false;
    }
  }

  _handleBack() {
    this.fireAncestors('$focusMenu');
    return true;
  }

  _getFocusable() {
    return this.tag('Content').children;
  }

  _centerVertical(centerY) {
    // Workaround for modals
    // Showing/hiding a modal will cause a refocus event, at which point this.finalH === 0
    // Prevent recentering in that case
    if (this.finalH === 0) return;

    const target = this.finalH / 2;

    const result = Math.min(
      0, //-this.tag('Content').finalH + this.tag('Content').finalH - constants.marginBottom,
      target - centerY
    );
    // console.log('centerVertical', centerY, target, result, this.tag('Content').finalH);

    this.tag('Content').setSmooth('y', result);
  }
}
